/**
 * ч.1120 (рішення власника: «запрет изменений — отдельной датой», як у 1С).
 *
 * Заборона змін — окрема дата організації: документи з датою до неї включно
 * не можна провести, розпровести чи змінити. Закриття місяця дату НЕ
 * пересуває — бухгалтер ставить її сам, коли період здано. Тут же підказка
 * «захистити останній закритий місяць».
 *
 * Блок видно тим, хто має право «Закриття періоду» в організації.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { periodLock, type PeriodLock } from '@/api/closing';
import DatePopover from '@/components/DatePopover';
import { useAllPermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { fmtDay } from './monthCloseLabels';

export const PERIOD_LOCK_PERMISSION = 'accounting.period.close';

export default function PeriodLockPanel({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const { permissionsByOrg } = useAllPermissionsByOrg();
  const allowed = (permissionsByOrg[orgId] ?? []).includes(PERIOD_LOCK_PERMISSION);
  const q = useQuery({
    queryKey: ['period-lock', orgId],
    queryFn: () => periodLock.get(orgId),
    enabled: !!orgId && allowed,
  });
  const [draft, setDraft] = useState('');
  useEffect(() => { setDraft(q.data?.lock_date ?? ''); }, [q.data?.lock_date]);

  const save = useMutation({
    mutationFn: (value: string | null) => periodLock.set(orgId, value),
    // Відповідь уже несе нову дату і підказку — кладемо її в кеш без перезапиту.
    onSuccess: (data: PeriodLock) => qc.setQueryData(['period-lock', orgId], data),
  });

  if (!orgId || !allowed) return null;
  const lock = q.data;
  const changed = !!draft && draft !== (lock?.lock_date ?? '');

  return (
    <div className="mc__panel" data-testid="period-lock-panel">
      <div className="mc__panel-head">
        Заборона змін
        {lock?.lock_date
          ? <span data-testid="period-lock-current">до {fmtDay(lock.lock_date)} включно</span>
          : <span data-testid="period-lock-current" style={{ color: 'var(--muted)' }}>не встановлена</span>}
        {lock?.lock_date && lock.set_by_name && (
          <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: '0.85rem' }}>
            встановив {lock.set_by_name}
            {lock.set_at ? `, ${new Date(lock.set_at).toLocaleDateString('uk-UA')}` : ''}
          </span>
        )}
      </div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '0.4rem' }}>
        Документи з датою до цієї дати включно не можна провести, розпровести чи змінити.
        Закриття місяця дату не пересуває — її встановлюють окремо, коли період здано.
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>нова дата</span>
        <DatePopover value={draft} onChange={setDraft} />
        <button type="button" className="primary" disabled={!changed || save.isPending}
          title={changed ? undefined : 'Оберіть дату, відмінну від чинної'}
          onClick={() => save.mutate(draft)}>
          встановити
        </button>
        <button type="button" disabled={!lock?.lock_date || save.isPending}
          title={lock?.lock_date ? 'Зняти заборону: усі періоди знову відкриті для змін'
            : 'Заборону не встановлено'}
          onClick={() => save.mutate(null)}>
          зняти
        </button>
        {lock?.suggested_lock_date && (
          <button type="button" className="an__link" disabled={save.isPending}
            data-testid="period-lock-suggest"
            title="Останній закритий місяць ще не захищений від змін"
            onClick={() => save.mutate(lock.suggested_lock_date)}>
            захистити закритий місяць — до {fmtDay(lock.suggested_lock_date)}
          </button>
        )}
      </div>
    </div>
  );
}
